import { Box, Paper, Grid, Typography } from "@mui/material";

import SubmittedEmail from "./SubmittedEmail";
import SubmittedAddress from "./SubmittedAddress";
import SubmittedPhone from "./SubmittedPhone";
import SubmittedPerson from "./SubmittedPerson";
import SubmittedDescription from "./SubmittedDescription";
import APIApplication from "../../../types/APIApplication";
import APIPhone from "../../../types/APIPhone";
import APIAddress from "../../../types/APIAddress";
import APIEmail from "../../../types/APIEmail";
import APIPerson from "../../../types/APIPerson";

// The four application columns used to be separated by <Divider orientation="vertical" />
// elements sitting directly inside the Grid container. Grid v2 cannot carry
// those: it lays children out with a real CSS `column-gap`, so every extra
// child costs a whole gap (40px here) rather than the Divider's 1px, and the
// row overflowed by exactly enough to wrap the fourth column onto its own
// line. GridLegacy tolerated it because its gutters were item padding, and
// the container's marginRight={-5} left 40px of slack to absorb the 3px of
// dividers.
//
// So the rule is drawn by the column it precedes instead, centred in the gap
// with a pseudo-element. Nothing but Grid items are children of the container
// now, which is the rule v2 actually enforces.
//
// `visibleFrom` is the breakpoint at which the column in question actually
// gains a neighbour on its left. Below it the columns are stacked and a rule
// would just hang down the left edge of the card.
const columnRule = (visibleFrom: "md" | "lg") => ({
  position: "relative" as const,
  "&::before": {
    content: '""',
    position: "absolute" as const,
    left: "-20px",
    top: 0,
    bottom: 0,
    borderLeft: "1px solid",
    borderColor: "divider",
    display: { xs: "none", [visibleFrom]: "block" },
  },
});

export default function Application({
  application,
  primary_phone_number,
  additional_phone_numbers,
  primary_address,
  additional_addresses,
  email_addresses,
  people,
}: {
  application: APIApplication | null;
  primary_phone_number: APIPhone | null;
  additional_phone_numbers: APIPhone[] | null;
  primary_address: APIAddress | null;
  additional_addresses: APIAddress[] | null;
  email_addresses: APIEmail[] | null;
  people: APIPerson[] | null;
}) {
  const getSubmittedAddress = () => {
    const addresses = additional_addresses || [];
    if (primary_address) {
      addresses.push(primary_address);
    }
    return addresses.find((addr) => addr.source === "submitted");
  };
  const submittedAddress = getSubmittedAddress();
  const submittedEmail = email_addresses?.find(
    (em) => em.source === "submitted"
  );
  const getsubmittedPhone = () => {
    const phones = [...(additional_phone_numbers || [])];
    if (primary_phone_number) {
      phones.push(primary_phone_number);
    }
    return phones.find((ph) => ph.source === "submitted");
  };
  const submittedPhone = getsubmittedPhone();
  const submittedPerson = people?.find(
    (person) => person.source === "submitted"
  );

  const submittedDescription = application?.description;

  if (
    ![
      submittedAddress,
      submittedPhone,
      submittedPerson,
      submittedEmail,
      submittedDescription,
    ].some(Boolean)
  ) {
    return null;
  }

  return (
    <Paper
      elevation={2}
      sx={{
        p: 0,
        overflow: "hidden",
      }}
    >
      <Box sx={{ pl: 3, pt: 3, pb: 3 }}>
        <Typography variant="h6" component="h3" sx={{ fontWeight: "normal" }}>
          Application
        </Typography>

        <Box sx={{ mt: 1 }}>
          <Grid
            container
            spacing={0}
            columnSpacing={{ xs: 0, md: 5 }}
            rowSpacing={{ xs: 0, md: 0 }}
            sx={{
              marginTop: 1,
            }}
          >
            <Grid
              size={{
                sm: 12,
                md: 6,
                lg: 3,
              }}
            >
              <SubmittedEmail
                email={submittedEmail}
                emailBusinessConnections={
                  application?.email_business_connections
                }
              />
            </Grid>
            <Grid
              size={{
                sm: 12,
                md: 6,
                lg: 3,
              }}
              sx={columnRule("md")}
            >
              <SubmittedPhone
                phone={submittedPhone}
                phoneBusinessConnections={
                  application?.phone_business_connections
                }
              />
            </Grid>
            <Grid
              size={{
                sm: 12,
                md: 6,
                lg: 3,
              }}
              sx={columnRule("lg")}
            >
              <SubmittedAddress
                address={submittedAddress}
                addressBusinessConnections={
                  application?.address_business_connections
                }
              />
            </Grid>
            <Grid
              size={{
                sm: 12,
                md: 6,
                lg: 3,
              }}
              sx={columnRule("md")}
            >
              <SubmittedPerson
                person={submittedPerson || null}
                personBusinessConnections={
                  application?.person_business_connections
                }
              />
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ mt: 1 }}>
          <Grid
            container
            spacing={0}
            columnSpacing={{ xs: 0, md: 5 }}
            rowSpacing={{ xs: 0, md: 0 }}
            sx={{
              marginTop: 1,
            }}
          >
            <Grid
              size={{
                sm: 12,
                md: 6,
                lg: 3,
              }}
            >
              <SubmittedDescription description={submittedDescription} />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Paper>
  );
}
